import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { EnfafButtonComponent } from '../../../../../../../components/enfaf-button/enfaf-button.component';
import { EnfafIconComponent } from '../../../../../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafModalComponent } from '../../../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafPaginationComponent, PaginationConfig } from '../../../../../../../components/enfaf-pagination/enfaf-pagination.component';
import { EnfafSearchableInputComponent } from '../../../../../../../components/enfaf-searchable-input/enfaf-searchable-input.component';
import { EnfafSelectComponent, EnfafSelectOption } from '../../../../../../../components/enfaf-select/enfaf-select.component';
import { ApiService, CourseMatriculationStudent } from '../../../../../../../core/services/api/api.service';
import { AlertService } from '../../../../../../../core/services/alert/alert.service';
import { ToastService } from '../../../../../../../core/services/toast/toast.service';
import { Course } from '../../../../../../../models/interfaces/course.interface';

type CourseMatriculationFilter = 'all' | 'finished' | 'not-finished';

@Component({
  selector: 'app-modal-course-students-matriculation',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatTooltipModule,
    TranslatePipe,
    EnfafButtonComponent,
    EnfafIconComponent,
    EnfafModalComponent,
    EnfafPaginationComponent,
    EnfafSearchableInputComponent,
    EnfafSelectComponent
  ],
  templateUrl: './modal-course-students-matriculation.component.html',
  styleUrl: './modal-course-students-matriculation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalCourseStudentsMatriculationComponent implements OnInit {
  protected readonly data = inject<Course>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<ModalCourseStudentsMatriculationComponent>);
  private readonly api = inject(ApiService);
  private readonly alert = inject(AlertService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  protected readonly filterControl = new FormControl<CourseMatriculationFilter>('finished', { nonNullable: true });
  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly filterOptions: EnfafSelectOption[] = [
    { value: 'all', viewValue: this.translate.instant('STUDENT.COURSE_MATRICULATION.FILTER_ALL') },
    { value: 'finished', viewValue: this.translate.instant('STUDENT.COURSE_MATRICULATION.FILTER_FINISHED') },
    { value: 'not-finished', viewValue: this.translate.instant('STUDENT.COURSE_MATRICULATION.FILTER_NOT_FINISHED') }
  ];
  protected readonly students = signal<CourseMatriculationStudent[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly isExporting = signal(false);
  protected readonly confirmingUserId = signal<string | null>(null);
  protected readonly completingUserId = signal<string | null>(null);
  protected readonly downloadingDiplomaUserId = signal<string | null>(null);
  protected readonly hasError = signal(false);
  protected readonly pageSize = signal(15);
  protected readonly currentPage = signal(1);
  private readonly selectedFilter = toSignal(this.filterControl.valueChanges, { initialValue: 'finished' as CourseMatriculationFilter });
  private readonly searchTerm = toSignal(this.searchControl.valueChanges, { initialValue: '' });

  protected readonly filteredStudents = computed(() => {
    const filter = this.selectedFilter();
    const search = this.searchTerm().trim().toLocaleLowerCase();

    return this.students().filter((student) => {
      const matchesFilter = filter === 'all' || (filter === 'finished' ? student.finished : !student.finished);
      return matchesFilter && (!search || this.getSearchableText(student).includes(search));
    });
  });

  protected readonly pagedStudents = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredStudents().slice(start, start + this.pageSize());
  });

  protected readonly paginationConfig = computed<PaginationConfig>(() => ({
    currentPage: this.currentPage(),
    totalItems: this.filteredStudents().length,
    itemsPerPage: this.pageSize(),
    maxVisiblePages: 6,
    showPageSize: true,
    pageSizeOptions: [15, 25, 50],
    showResultsCount: true
  }));

  protected readonly emptyStateMessage = computed(() => {
    const filter = this.selectedFilter();
    if (filter === 'finished') return 'STUDENT.COURSE_MATRICULATION.EMPTY_FINISHED';
    if (filter === 'not-finished') return 'STUDENT.COURSE_MATRICULATION.EMPTY_NOT_FINISHED';
    return 'STUDENT.COURSE_MATRICULATION.EMPTY';
  });

  protected readonly resetPageOnFilterChange = effect(() => {
    this.filteredStudents();
    this.currentPage.set(1);
  });

  ngOnInit(): void {
    void this.loadStudents();
  }

  protected formatFinishedDate(value: string | null): string {
    if (!value) return '-';

    const date = DateTime.fromISO(value, { zone: 'utc' });
    return date.isValid ? date.toFormat('dd/LL/yy') : '-';
  }

  protected onPageChange(currentPage: number): void {
    this.currentPage.set(currentPage);
  }

  protected onPageSizeChange(pageSize: number): void {
    this.pageSize.set(pageSize);
    this.currentPage.set(1);
  }

  protected closeModal(): void {
    this.dialogRef.close();
  }

  protected async exportExcel(): Promise<void> {
    if (this.isExporting() || !this.students().length) return;

    this.isExporting.set(true);
    try {
      await this.api.exportCourseMatriculationExcel(this.data.courseId);
      this.dialogRef.close({ exported: true });
    } catch (error: any) {
      this.toast.presentErrorToast(error?.message || error?.error?.message || this.translate.instant('STUDENT.COURSE_MATRICULATION.LOAD_ERROR'));
    } finally {
      this.isExporting.set(false);
    }
  }

  protected async confirmCourseCompletion(userId: string): Promise<void> {
    if (this.completingUserId() || this.confirmingUserId()) return;

    this.confirmingUserId.set(userId);
    try {
      const confirmed = await this.alert.presentConfirmActionAlert(
        this.translate.instant('STUDENT.COURSE_MATRICULATION.COMPLETE_CONFIRM_TITLE'),
        this.translate.instant('STUDENT.COURSE_MATRICULATION.COMPLETE_CONFIRM_MESSAGE'),
        this.translate.instant('COMMON.CANCEL'),
        this.translate.instant('COMMON.ACCEPT')
      );

      if (confirmed) await this.completeCourse(userId);
    } finally {
      this.confirmingUserId.set(null);
    }
  }

  protected async completeCourse(userId: string): Promise<void> {
    if (this.completingUserId()) return;

    this.completingUserId.set(userId);
    try {
      const response = await firstValueFrom(this.api.completeStudentCourse({ userId, courseId: this.data.courseId }));
      const finishedDate = response.student?.finishedDate ?? null;
      this.students.update((students) =>
        students.map((student) => (student.userId === userId ? { ...student, finished: true, finishedDate } : student))
      );
      this.toast.presentSuccessToast(this.translate.instant('STUDENT.COURSE_MATRICULATION.COMPLETE_SUCCESS'));
    } catch (error: any) {
      this.toast.presentErrorToast(error?.error?.message || error?.message || this.translate.instant('STUDENT.COURSE_MATRICULATION.COMPLETE_ERROR'));
    } finally {
      this.completingUserId.set(null);
    }
  }

  protected async downloadDiploma(userId: string): Promise<void> {
    if (this.downloadingDiplomaUserId()) return;

    this.downloadingDiplomaUserId.set(userId);
    try {
      const downloaded = await this.api.downloadCourseStudentDiploma({ courseId: this.data.courseId, userId });
      if (downloaded) {
        this.toast.presentSuccessToast(this.translate.instant('STUDENT.COURSE_MATRICULATION.DOWNLOAD_SUCCESS'));
      } else {
        this.toast.presentErrorToast(this.translate.instant('STUDENT.COURSE_MATRICULATION.DOWNLOAD_ERROR'));
      }
    } catch (error: any) {
      this.toast.presentErrorToast(error?.error?.message || error?.message || this.translate.instant('STUDENT.COURSE_MATRICULATION.DOWNLOAD_ERROR'));
    } finally {
      this.downloadingDiplomaUserId.set(null);
    }
  }

  private async loadStudents(): Promise<void> {
    try {
      const pageSize = 100;
      const firstResponse = await firstValueFrom(this.api.fetchCourseMatriculationStudents(this.data.courseId, { page: 0, pageSize }));
      const total = Number(firstResponse.students?.count ?? 0);
      const students = [...(firstResponse.students?.rows ?? [])];
      let page = 1;

      while (students.length < total) {
        const response = await firstValueFrom(this.api.fetchCourseMatriculationStudents(this.data.courseId, { page, pageSize }));
        const rows = response.students?.rows ?? [];
        if (!rows.length) break;
        students.push(...rows);
        page += 1;
      }

      this.students.set(students);
      this.isLoading.set(false);
    } catch (error: any) {
      this.hasError.set(true);
      this.isLoading.set(false);
      this.toast.presentErrorToast(error?.error?.message || error?.message || this.translate.instant('STUDENT.COURSE_MATRICULATION.LOAD_ERROR'));
    }
  }

  private getSearchableText(student: CourseMatriculationStudent): string {
    const status = student.finished ? 'sí yes' : 'no';
    return `${student.name} ${student.lastname} ${student.email ?? ''} ${student.phonePrefix ?? ''} ${student.phone ?? ''} ${student.totalClasses} ${status} ${this.formatFinishedDate(student.finishedDate)}`.toLocaleLowerCase();
  }
}
